// Adapted from https://gist.github.com/gaearon/830490fc17d3fccc88c9

import type { HTMLProps } from "react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

type AudioProps = Omit<HTMLProps<HTMLAudioElement>, "onError" | "onProgress" | "onTimeUpdate"> & {
  defaultTime: number;
  isPlaying: boolean;
  onEnd: () => void;
  onError: () => void;
  onProgress: (progress: OnProgressProps) => void;
  onTimeUpdate: (value: OnTimeUpdateProps) => void;
  source: string;
};

export type OnTimeUpdateProps = { buffered: TimeRanges; currentTime: number; trackDuration: number };
export type OnProgressProps = { buffered: TimeRanges; trackDuration: number };
export type AudioRef = { updateTimeBySeconds: (time: number) => void };

export const Audio = forwardRef<AudioRef, AudioProps>(
  (
    {
      source,
      isPlaying,
      defaultTime,
      onProgress = () => null,
      onTimeUpdate = () => null,
      onEnd = () => null,
      onError,
      ...props
    },
    ref,
  ) => {
    useImperativeHandle(ref, () => ({
      updateTimeBySeconds,
    }));

    const elementRef = useRef<HTMLAudioElement>(null);
    const playPausePromiseRef = useRef<Promise<unknown> | undefined>(undefined);

    const handleProgress = useCallback(() => {
      const node = elementRef.current;
      if (!node || !onProgress) {
        return;
      }

      const { buffered } = node;
      const trackDuration = node.duration;
      onProgress({ buffered, trackDuration });
    }, [onProgress]);

    const handleTimeUpdate = useCallback(() => {
      const node = elementRef.current;
      if (!node || !onTimeUpdate) {
        return;
      }

      const { currentTime } = node;
      const trackDuration = node.duration;
      const { buffered } = node;
      onTimeUpdate({ currentTime, trackDuration, buffered });
    }, [onTimeUpdate]);

    const handleMediaEnd = useCallback(() => {
      const node = elementRef.current;
      if (!node) {
        return;
      }

      node.currentTime = 0;
      if (onEnd) {
        onEnd();
      }
    }, [onEnd]);

    const updateCurrentTime = useCallback(() => {
      const node = elementRef.current;
      if (!node?.readyState) {
        return;
      }

      node.currentTime = defaultTime;
    }, [defaultTime]);

    const _play = () => Promise.resolve(elementRef.current?.play());

    const _pause = () => Promise.resolve(elementRef.current?.pause());

    const _load = () => Promise.resolve(elementRef.current?.load());

    const playPause = useCallback(() => {
      const node = elementRef.current;
      if (!node) {
        return;
      }

      let playPausePromise = Promise.resolve();
      if (node && !node.readyState) {
        playPausePromise = _load();
      }

      if (isPlaying) {
        playPausePromise = playPausePromise.then(_play);
      } else {
        playPausePromise = playPausePromise.then(_pause);
      }
      playPausePromiseRef.current = playPausePromise.catch(onError);
      return playPausePromiseRef.current;
    }, [isPlaying, onError]);

    const updateIsPlaying = useCallback(() => {
      const node = elementRef.current;
      if (!node) {
        return;
      }

      if (playPausePromiseRef.current) {
        playPausePromiseRef.current = playPausePromiseRef.current.then(playPause);
        return playPausePromiseRef.current;
      }
      return playPause();
    }, [playPause]);

    const updateTimeBySeconds = (newTime: number) => {
      if (elementRef.current) {
        elementRef.current.currentTime = elementRef.current.currentTime + newTime;
      }
    };

    const updateSource = useCallback(() => {
      const node = elementRef.current;
      if (!node?.readyState) {
        return;
      }

      try {
        let playPausePromise: Promise<unknown>;
        if (playPausePromiseRef.current) {
          playPausePromise = playPausePromiseRef.current.then(_pause);
        } else {
          playPausePromise = _pause();
        }
        playPausePromise
          .then(() => {
            if (onTimeUpdate) {
              onTimeUpdate({
                currentTime: 0,
                trackDuration: node.duration,
                buffered: node.buffered,
              });
            }
            return _load().then(updateIsPlaying);
          })
          .catch(onError);
        playPausePromiseRef.current = playPausePromise;
      } catch {
        onError?.();
      }
    }, [updateIsPlaying, onTimeUpdate, onError]);

    useEffect(() => {
      // Store a reference to currentElement to be able to access
      // the same node later in the cleanup (in case it has changed)
      const node = elementRef.current;
      if (!node) {
        return;
      }

      node.addEventListener("progress", handleProgress);
      node.addEventListener("timeupdate", handleTimeUpdate);
      node.addEventListener("ended", handleMediaEnd);

      void updateIsPlaying();

      return () => {
        node.removeEventListener("progress", handleProgress);
        node.removeEventListener("timeupdate", handleTimeUpdate);
        node.removeEventListener("ended", handleMediaEnd);
      };
    }, [handleProgress, handleTimeUpdate, handleMediaEnd, updateIsPlaying]);

    useEffect(() => {
      updateSource();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);

    useEffect(() => {
      void updateIsPlaying();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying]);

    useEffect(updateCurrentTime, [updateCurrentTime]);

    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <audio ref={elementRef} {...props} src={source} />;
  },
);

Audio.displayName = "Audio";
